import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CART_STORAGE_KEY = 'mobiluxe_cart_v1';
const MAX_CART_QUANTITY_PER_PRODUCT = 99;

const CartContext = createContext(null);

const parseStoredCart = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() =>
    typeof window !== 'undefined' ? parseStoredCart() : []
  );

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1) => {
    if (!product?.id) return;
    const parsedQuantity = Math.floor(Number(quantity));
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return;

    setItems((prev) => {
      const existingIndex = prev.findIndex((entry) => String(entry.product.id) === String(product.id));
      if (existingIndex >= 0) {
        return prev.map((entry, index) =>
          index === existingIndex
            ? {
                ...entry,
                quantity: Math.min(
                  MAX_CART_QUANTITY_PER_PRODUCT,
                  Math.max(1, Number(entry.quantity || 0) + parsedQuantity)
                ),
              }
            : entry
        );
      }

      return [
        ...prev,
        {
          product,
          quantity: Math.min(MAX_CART_QUANTITY_PER_PRODUCT, Math.max(1, parsedQuantity)),
        },
      ];
    });
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((entry) => String(entry.product.id) !== String(productId)));
  };

  const updateQuantity = (productId, quantity) => {
    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity)) return;

    if (parsedQuantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems((prev) =>
      prev.map((entry) =>
        String(entry.product.id) === String(productId)
          ? {
              ...entry,
              quantity: Math.min(MAX_CART_QUANTITY_PER_PRODUCT, Math.max(1, Math.floor(parsedQuantity))),
            }
          : entry
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const itemCount = useMemo(
    () => items.reduce((acc, entry) => acc + Number(entry.quantity || 0), 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce((acc, entry) => {
        const price = Number(entry.product?.price || 0);
        const quantity = Number(entry.quantity || 0);
        return acc + price * quantity;
      }, 0),
    [items]
  );

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
    subtotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
